import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/**
 * DangerZone — permanent deletion of courses and instructor accounts. Admin only.
 *
 * The flow is deliberately three steps: choose a target, read a preview of what
 * will be destroyed, then type the course code or account email to confirm.
 * window.confirm is fine for reversible settings but stops being read by the
 * third dialog of the day, and none of this is reversible.
 *
 * Props:
 *   courses     — [{ id, course_code, course_name, is_active }] from /admin/overview
 *   instructors — [{ id, legal_name, display_name, email, role }] from /admin/overview
 *   onChanged   — called after a successful delete so the parent can reload
 */
export default function DangerZone({ courses = [], instructors = [], onChanged }) {
  const { apiFetch } = useAuth();

  const [open,    setOpen]    = useState(false);
  const [kind,    setKind]    = useState('course');   // course | instructor
  const [targetId, setTargetId] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [cascade, setCascade] = useState(false);
  const [retentionOk, setRetentionOk] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [done,    setDone]    = useState('');

  const reset = () => {
    setTargetId(''); setPreview(null); setConfirm('');
    setCascade(false); setRetentionOk(false); setError('');
  };

  const loadPreview = async (id) => {
    setTargetId(id); setPreview(null); setConfirm(''); setError('');
    setCascade(false); setRetentionOk(false);
    if (!id) return;
    setBusy(true);
    try {
      // Course operations go through the course router, which is the single
      // owner of course deletion — the instructor's own delete in Manage hits
      // the same endpoint with different privileges. Accounts stay on the admin
      // router, since there's no instructor equivalent.
      const path = kind === 'course'
        ? `/api/courses/${id}/delete-preview`
        : `/api/admin/instructors/${id}/delete-preview`;
      const res = await apiFetch(path);
      const d = await res.json().catch(() => ({}));
      if (res.ok) setPreview(d);
      else setError(typeof d.detail === 'string' ? d.detail : 'Could not load the preview.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const expected = !preview ? '' : (kind === 'course' ? preview.course_code : preview.email);
  const matches  = confirm.trim().toLowerCase() === String(expected).toLowerCase();

  const runDelete = async () => {
    setBusy(true); setError(''); setDone('');
    try {
      const params = new URLSearchParams({ confirm: confirm.trim() });
      let path;
      if (kind === 'course') {
        // force waives the archive-first and live-queue guards. An admin
        // clearing out a departed instructor's course shouldn't have to archive
        // it first; the instructor's own path in Manage still must.
        params.set('force', 'true');
        path = `/api/courses/${targetId}?${params}`;
      } else {
        if (cascade)     params.set('cascade_courses', 'true');
        if (retentionOk) params.set('retention_acknowledged', 'true');
        path = `/api/admin/instructors/${targetId}?${params}`;
      }

      const res = await apiFetch(path, { method: 'DELETE' });
      const d = await res.json().catch(() => ({}));

      if (res.ok) {
        setDone(d.message || 'Deleted.');
        reset();
        onChanged?.();
        setTimeout(() => setDone(''), 8000);
        return;
      }

      // 409 returns { message, blockers } so the UI can say what's in the way
      // rather than "conflict".
      const detail = d.detail;
      if (detail && typeof detail === 'object') {
        const blockers = (detail.blockers || [])
          .map(b => b.course_code || b.type)
          .join(', ');
        setError(blockers ? `${detail.message} (${blockers})` : detail.message);
      } else {
        setError(typeof detail === 'string' ? detail : 'Delete failed.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const targets = kind === 'course' ? courses : instructors;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle} style={{ color: 'var(--red, #e5484d)' }}>
        Danger Zone
      </h3>
      <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 10 }}>
        Permanently delete a course or an account. There's no undo and no backup —
        to make something disappear temporarily, archive it in Manage instead.
        Instructors can delete their own courses from Manage without coming here.
      </p>

      {done && <div className={styles.success} style={{ marginBottom: 10 }}>✓ {done}</div>}

      {!open ? (
        <button className="btn-ghost" onClick={() => setOpen(true)}
          style={{ color: 'var(--red, #e5484d)', fontSize: 12 }}>
          Show deletion tools
        </button>
      ) : (
        <div style={{
          padding: 14, borderRadius: 8,
          border: '1px solid var(--red, #e5484d)44',
          background: 'var(--red, #e5484d)08',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {['course', 'instructor'].map(k => (
              <button key={k}
                className={kind === k ? 'btn-primary' : 'btn-ghost'}
                onClick={() => { setKind(k); reset(); }}
                style={{ fontSize: 12 }}>
                {k === 'course' ? 'Course' : 'Account'}
              </button>
            ))}
          </div>

          <div className={styles.field}>
            <label>{kind === 'course' ? 'Course to delete' : 'Account to delete'}</label>
            <select value={targetId} onChange={e => loadPreview(e.target.value)}
              style={selectStyle}>
              <option value="">Choose one…</option>
              {targets.map(t => (
                <option key={t.id} value={t.id}>
                  {kind === 'course'
                    ? `${t.course_code} — ${t.course_name}${t.is_active ? '' : ' (archived)'}`
                    : `${t.legal_name || t.display_name} — ${t.email}`}
                </option>
              ))}
            </select>
          </div>

          {busy && !preview && (
            <p style={{ fontSize: 12, color: 'var(--txt-dim)' }}>Loading…</p>
          )}

          {preview && (
            <>
              <div style={previewBox}>
                <div style={{ color: 'var(--txt)', fontWeight: 600, marginBottom: 6 }}>
                  This will permanently destroy:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--txt-faint)' }}>
                  {kind === 'course' ? (
                    <>
                      <li>{preview.counts.enrollments} enrollment(s)</li>
                      <li>{preview.counts.queue_entries} queue entries</li>
                      <li>{preview.counts.competencies} course competencies</li>
                      <li>{preview.counts.student_wait_points} wait-point records</li>
                      <li>{preview.counts.invite_codes} invite code(s)</li>
                    </>
                  ) : (
                    <>
                      <li>{preview.total_sessions} queue session(s)</li>
                      <li>{preview.enrollments} enrollment(s)</li>
                      <li>{preview.invite_codes_destroyed} invite code(s) they issued</li>
                      {preview.owned_courses.length > 0 && (
                        <li style={{ color: 'var(--red, #e5484d)' }}>
                          {preview.owned_courses.length} course(s) they own:{' '}
                          {preview.owned_courses.map(c => c.course_code).join(', ')}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>

              {/* The records warning. This is the one that should be hard to skim -
                  the admin is destroying records they aren't the custodian of. */}
              {kind === 'instructor' && preview.audit_logs_destroyed > 0 && (
                <div style={auditBox}>
                  <strong style={{ color: 'var(--txt)' }}>
                    {preview.audit_logs_destroyed.toLocaleString()} queue log entries
                    across {preview.total_sessions} session(s) will be destroyed.
                  </strong>
                  <br />
                  These are institutional records, not this account's property.
                  Check that no retention schedule, grade appeal, conduct case,
                  open records request or legal hold covers them — you may know
                  about holds the instructor doesn't. Export first if you need a copy.
                </div>
              )}

              {preview.students_waiting_now > 0 && (
                <div style={auditBox}>
                  {preview.students_waiting_now} student(s) are in a live queue for
                  this course right now. Close the session before deleting it.
                </div>
              )}

              {kind === 'instructor' && preview.owned_courses.length > 0 && (
                <label style={checkRow}>
                  <input type="checkbox" checked={cascade}
                    onChange={e => setCascade(e.target.checked)} style={checkBox} />
                  Also delete the {preview.owned_courses.length} course(s) they own
                </label>
              )}

              {kind === 'instructor' && preview.audit_logs_destroyed > 0 && (
                <label style={checkRow}>
                  <input type="checkbox" checked={retentionOk}
                    onChange={e => setRetentionOk(e.target.checked)} style={checkBox} />
                  No retention schedule or legal hold covers these records
                </label>
              )}

              <div className={styles.field} style={{ marginTop: 10 }}>
                <label>
                  Type <strong style={{ color: 'var(--txt)' }}>{expected}</strong> to confirm
                </label>
                <input value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder={expected} autoComplete="off" spellCheck={false} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn-primary" onClick={runDelete}
                  disabled={busy || !matches}
                  style={matches ? { background: 'var(--red, #e5484d)', borderColor: 'var(--red, #e5484d)' } : undefined}>
                  {busy ? 'Deleting…' : `Delete ${expected} permanently`}
                </button>
                <button className="btn-ghost" onClick={reset} disabled={busy}>
                  Cancel
                </button>
              </div>
            </>
          )}

          {error && <div className={styles.error} style={{ marginTop: 12 }}>⚠ {error}</div>}

          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <button className="btn-ghost" onClick={() => { setOpen(false); reset(); }}
              style={{ fontSize: 12 }}>
              Hide deletion tools
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const selectStyle = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--txt)', fontSize: 13, padding: '8px 10px', fontFamily: 'inherit',
};

const previewBox = {
  fontSize: 12, marginTop: 10, padding: '10px 12px', borderRadius: 6,
  background: 'var(--bg-card)', border: '1px solid var(--border)',
};

const auditBox = {
  fontSize: 12, marginTop: 8, padding: '10px 12px', borderRadius: 6,
  background: 'var(--red, #e5484d)12', color: 'var(--txt-dim)',
  border: '1px solid var(--red, #e5484d)44', lineHeight: 1.5,
};

const checkRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 12, color: 'var(--txt-dim)', marginTop: 10,
};

const checkBox = { width: 16, height: 16, flexShrink: 0, margin: 0 };
